import {
  Avatar,
  Badge,
  Box,
  Button,
  FlexBox,
  IconButton,
  List,
} from '@dixa/lib-shared-habla';
import { SearchResultEnriched } from 'lib-search-types';
import { DeepWritable } from 'ts-essentials';

import { numberFormatterInternational } from 'app/utils/telephonyHelpers';
import { AppLink } from 'components/appLink';
import { Highlight } from 'components/highlight';
import { getUserDisplayName } from 'utils/user';
import { userTypeAssets } from 'utils/user/userAssets';

import styles from './contactPreview.module.scss';

type UserResult = SearchResultEnriched<'users'>;
type CompanyResult = SearchResultEnriched<'companies'>;

type ContactPreviewProps = {
  result: UserResult | CompanyResult | null;
  onGoToContact: VoidFunction;
  onPreviewClose: VoidFunction;
};

const isUser = (result: UserResult | CompanyResult): result is UserResult =>
  typeof (result as UserResult).result.type !== 'undefined';

/** Represents a component that is used to preview an individual contact */
export const ContactPreview = ({
  result,
  onGoToContact,
  onPreviewClose,
}: ContactPreviewProps): JSX.Element | null => {
  if (!result) return null;

  const {
    name,
    email,
    phone_number,
    additional_emails,
    additional_phone_numbers,
  } = isUser(result)
    ? {
        name: getUserDisplayName(result.result),
        email: result.highlights?.email?.[0] || result.result.email,
        phone_number:
          result.highlights?.phone_number?.[0] || result.result.phone_number,
        additional_emails:
          result.highlights?.additional_emails ??
          result.result.additional_emails,
        additional_phone_numbers:
          result.highlights?.additional_phone_numbers ??
          result.result.additional_phone_numbers,
      }
    : {
        name: result.highlights?.name?.[0] || result.result.name || '',
        email: null,
        phone_number: null,
        additional_emails: null,
        additional_phone_numbers: null,
      };

  const renderUserDetails = (): JSX.Element | null => {
    if (!isUser(result)) return null;

    const items = [
      {
        key: additional_emails?.length ? 'Emails' : 'Email',
        value: email ? (
          <FlexBox flexDirection="column" gap="200">
            <FlexBox alignItems="center" gap="200">
              <Highlight highlightedValue={email} />

              {additional_emails?.length ? (
                <Badge variant="primary" size="small">
                  Primary
                </Badge>
              ) : null}
            </FlexBox>

            {additional_emails?.map((email) => (
              <Highlight key={email} highlightedValue={email} />
            ))}
          </FlexBox>
        ) : null,
      },
      {
        key: additional_phone_numbers?.length
          ? 'Phone numbers'
          : 'Phone number',
        value: phone_number ? (
          <FlexBox flexDirection="column" gap="200">
            <FlexBox alignItems="center" gap="200">
              <Highlight
                highlightedValue={numberFormatterInternational({
                  phoneNumber: phone_number,
                })}
              />

              {additional_phone_numbers?.length ? (
                <Badge variant="primary" size="small">
                  Primary
                </Badge>
              ) : null}
            </FlexBox>

            {additional_phone_numbers?.map((phone_number) => (
              <Highlight
                key={phone_number}
                highlightedValue={numberFormatterInternational({
                  phoneNumber: phone_number,
                })}
              />
            ))}
          </FlexBox>
        ) : null,
      },
      {
        key: 'User category',
        value: userTypeAssets[result.result.type]?.prettyName,
      },
    ];

    return (
      <Box margin="300 0 0 0" padding="0 300">
        <List>
          {items.map((item, index) => (
            <List.KeyValue key={index} pair={item} withHoverEffect={false} />
          ))}
        </List>
      </Box>
    );
  };

  return (
    <>
      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        padding="300 400"
      >
        <div className={styles.boldText}>Contact</div>

        <div className={styles.actions}>
          <AppLink
            path={
              isUser(result) ? '/contacts/user/:id' : '/contacts/company/:id'
            }
            params={{
              path: { id: result.result.id },
            }}
            onClick={onGoToContact}
          >
            <Button variant="secondary">Go to contact</Button>
          </AppLink>

          <IconButton
            icon="close"
            variant="secondary"
            onClick={onPreviewClose}
          />
        </div>
      </FlexBox>

      <FlexBox alignItems="center" padding="0 300">
        <Avatar
          user={result.result as DeepWritable<typeof result.result>}
          className={styles.avatar}
        />

        <div>
          <div className={styles.boldText}>
            <Highlight highlightedValue={name} />
          </div>

          {email && (
            <div className={styles.grayText}>
              <Highlight highlightedValue={email} />
            </div>
          )}
        </div>
      </FlexBox>

      {renderUserDetails()}
    </>
  );
};
